/**
 * @file Safe access to the analytics API exposed by SitePageTracker.
 */

/**
 * Call a PtEvents method when SitePageTracker has made it available.
 *
 * Analytics must never prevent a component from doing its primary job, so
 * failures in the tracker are treated the same as an unavailable tracker.
 *
 * @param {string} method - PtEvents method name.
 * @param {Array} args - Arguments passed to the method.
 * @returns {boolean} Whether the method was called successfully.
 */
const callPtEvents = (method, args) => {
	if (
		typeof window === 'undefined' ||
		!window.PtEvents ||
		typeof window.PtEvents[method] !== 'function'
	) {
		return false;
	}

	try {
		window.PtEvents[method](...args);
		return true;
	} catch {
		return false;
	}
};

/**
 * Log an analytics event when SitePageTracker is available.
 *
 * @param {string} category - Event category.
 * @param {string} name - Event name.
 * @param {string[]} tags - Cross-cutting event tags.
 * @param {*} data - Event-specific data.
 * @returns {boolean} Whether the event was logged successfully.
 */
const logPtEvent = (category, name, tags = [], data) =>
	callPtEvents('logEvent', [category, name, tags, data]);

/**
 * Log an analytics error when SitePageTracker is available.
 *
 * @param {string} category - Error category.
 * @param {string} name - Error name.
 * @param {*} data - Error-specific data.
 * @param {*} exception - Original exception, when available.
 * @returns {boolean} Whether the error was logged successfully.
 */
const logPtError = (category, name, data, exception) =>
	callPtEvents('logError', [category, name, data, exception]);

export { logPtError, logPtEvent };
